import React, { useState, useEffect } from 'react';
import API from '../../../services/api';

const BookingForm = ({ tour, bookingData, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    pickup_location: '',
    special_requirements: ''
  });

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const numSingle = bookingData.numSingleAtvs || 0;
    const numDouble = bookingData.numDoubleAtvs || 0;

    const initialParticipants = [];
    let atvCounter = 1;

    // Generate riders for single ATVs
    for (let i = 0; i < numSingle; i++) {
      initialParticipants.push({
        full_name: '',
        email: '',
        phone: '',
        age: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        medical_conditions: '',
        atv_type: 'single',
        atv_number: atvCounter++,
        is_single: true
      });
    }

    // Generate riders + passengers for double ATVs
    for (let i = 0; i < numDouble; i++) {
      initialParticipants.push({
        full_name: '',
        email: '',
        phone: '',
        age: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        medical_conditions: '',
        atv_type: 'double',
        atv_number: atvCounter,
        is_single: false,
        passenger: {
          full_name: '',
          email: '',
          phone: '',
          age: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          medical_conditions: ''
        }
      });
      atvCounter++;
    }

    setParticipants(initialParticipants);
  }, [bookingData.numSingleAtvs, bookingData.numDoubleAtvs]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleParticipantChange = (riderIndex, field, value) => {
    const updated = [...participants];
    updated[riderIndex][field] = value;
    setParticipants(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const allParticipants = [];
      let numPassengers = 0;

      participants.forEach((rider, riderIndex) => {
        allParticipants.push({
          ...rider,
          is_rider: 1,
          is_passenger: 0
        });

        // ✅ FIX: Check for passengers on ANY double ATV (not just pure 'shared' bookings)
        if (!rider.is_single && rider.passenger && rider.passenger.full_name) {
          allParticipants.push({
            ...rider.passenger,
            is_rider: 0,
            is_passenger: 1,
            rider_index: riderIndex
          });
          numPassengers++;
        }
      });

      const bookingPayload = {
        tour_id: tour.id,
        availability_id: bookingData.availabilityId,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        pickup_location: formData.pickup_location,
        ride_type: bookingData.rideType,
        num_atvs: bookingData.numAtvs,
        num_single_atvs: bookingData.numSingleAtvs || 0,
        num_double_atvs: bookingData.numDoubleAtvs || 0,
        num_passengers: numPassengers,
        special_requirements: formData.special_requirements,
        participants: allParticipants
      };

      const response = await API.bookings.create(bookingPayload);

      if (response.success && response.data) {
        const bookingResponse = response.data;

        if (!bookingResponse.participants || bookingResponse.participants.length === 0) {
          setError('Booking created but participant data is missing. Please contact support.');
          return;
        }

        onComplete({
          reference: bookingResponse.booking_reference,
          customerInfo: {
            name: formData.customer_name,
            email: formData.customer_email,
            phone: formData.customer_phone,
            pickup_location: formData.pickup_location,
            special_requirements: formData.special_requirements
          },
          participants: bookingResponse.participants,
          tour: tour,
          selectedDate: bookingResponse.formatted_date || bookingData.selectedDate,
          selectedTime: bookingResponse.formatted_time || bookingData.selectedTime,
          selectedSlot: bookingData.selectedSlot,
          availabilityId: bookingData.availabilityId,
          rideType: bookingData.rideType,
          numAtvs: bookingData.numAtvs,
          numRiders: bookingData.numRiders,
          numPassengers: numPassengers,
          totalAmount: parseFloat(bookingResponse.total_amount || bookingData.totalAmount || 0),
          pricePerAtv: bookingData.pricePerAtv,
          basePrice: bookingData.basePrice,
          sharedFee: bookingData.sharedFee
        });
      } else {
        setError(response.message || 'Failed to create booking');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating your booking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-8 pb-16">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-playfair font-bold text-white mb-2">
            Rider & Contact Details
          </h2>
          <p className="text-gray-400">Please provide information for all participants</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Contact Information */}
          <div className="bg-gray-800 rounded-2xl p-6 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">Primary Contact</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.customer_email}
                  onChange={(e) => handleInputChange('customer_email', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.customer_phone}
                  onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pickup Location *
                </label>
                <select
                  value={formData.pickup_location}
                  required
                  onChange={(e) => handleInputChange('pickup_location', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select pickup location</option>
                  {tour.pickup_locations && tour.pickup_locations.map((location, idx) => (
                    <option key={idx} value={location}>{location}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Special Requirements
              </label>
              <textarea
                value={formData.special_requirements}
                onChange={(e) => handleInputChange('special_requirements', e.target.value)}
                rows="3"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                placeholder="Any special requests or requirements?"
              />
            </div>
          </div>

          {/* Rider Details */}
          {participants.map((rider, riderIndex) => (
            <div key={riderIndex} className="bg-gray-800 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                  {rider.atv_number || (riderIndex + 1)}
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">
                    {rider.is_single ? '🏍️' : '👥'} ATV #{rider.atv_number || (riderIndex + 1)} - Rider
                  </h4>
                  <p className="text-sm text-gray-400">
                    {rider.is_single
                      ? 'Single ATV (solo rider only)'
                      : 'Double ATV (rider + optional passenger)'}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={rider.full_name}
                    onChange={(e) => handleParticipantChange(riderIndex, 'full_name', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="Rider's full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Age *
                  </label>
                  <input
                    type="number"
                    required
                    min={tour.min_age || 16}
                    value={rider.age}
                    onChange={(e) => handleParticipantChange(riderIndex, 'age', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder={`Min age: ${tour.min_age || 16}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={rider.email}
                    onChange={(e) => handleParticipantChange(riderIndex, 'email', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="rider@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={rider.phone}
                    onChange={(e) => handleParticipantChange(riderIndex, 'phone', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Emergency Contact Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={rider.emergency_contact_name}
                    onChange={(e) => handleParticipantChange(riderIndex, 'emergency_contact_name', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="Emergency contact"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Emergency Contact Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={rider.emergency_contact_phone}
                    onChange={(e) => handleParticipantChange(riderIndex, 'emergency_contact_phone', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder="+1 (555) 987-6543"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Medical Conditions or Allergies
                  </label>
                  <textarea
                    value={rider.medical_conditions}
                    onChange={(e) => handleParticipantChange(riderIndex, 'medical_conditions', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder="Any medical conditions we should know about"
                  />
                </div>
              </div>

              {/* Passenger Section (Only for Shared Rides) */}
              {!rider.is_single && rider.passenger && (
                <div className="mt-6 pt-6 border-t border-gray-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold">
                      P
                    </div>
                    <div>
                      <h5 className="text-lg font-bold text-white">
                        Passenger for ATV #{rider.atv_number || (riderIndex + 1)}
                      </h5>
                      <p className="text-sm text-gray-400">Optional - leave blank if riding solo</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 bg-gray-700/50 p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={rider.passenger.full_name}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.full_name = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="Passenger name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Age *
                      </label>
                      <input
                        type="number"
                        required
                        min="5"
                        value={rider.passenger.age}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.age = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="Passenger age"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={rider.passenger.email}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.email = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="passenger@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={rider.passenger.phone}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.phone = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Emergency Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={rider.passenger.emergency_contact_name}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.emergency_contact_name = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="Emergency contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Emergency Contact Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={rider.passenger.emergency_contact_phone}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.emergency_contact_phone = e.target.value;
                          setParticipants(updated);
                        }}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        placeholder="+1 (555) 987-6543"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Medical Conditions or Allergies
                      </label>
                      <textarea
                        value={rider.passenger.medical_conditions}
                        onChange={(e) => {
                          const updated = [...participants];
                          updated[riderIndex].passenger.medical_conditions = e.target.value;
                          setParticipants(updated);
                        }}
                        rows="2"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                        placeholder="Any medical conditions we should know about"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Booking Summary */}
          <div className="bg-gray-800 rounded-2xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Booking Summary</h3>
            <div className="space-y-2 text-gray-300">
              <div className="flex justify-between">
                <span>Tour:</span>
                <span className="font-bold text-white">{tour.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Date & Time:</span>
                <span className="font-bold text-white">
                  {bookingData.selectedSlot?.formatted_date} at {bookingData.selectedSlot?.formatted_time}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Ride Type:</span>
                <span className="font-bold">
                  {bookingData.rideType === 'single' ? 'Single' : 'Shared'} Ride
                </span>
              </div>
              <div className="flex justify-between">
                <span>ATVs:</span>
                <span className="font-bold">{bookingData.numAtvs}</span>
              </div>
              {bookingData.rideType === 'shared' && (
                <div className="flex justify-between">
                  <span>Passengers:</span>
                  <span className="font-bold text-gray-400">
                    {participants.filter(p => p.passenger && p.passenger.full_name).length} (optional)
                  </span>
                </div>
              )}
              <div className="border-t border-gray-600 mt-4 pt-4">
                <div className="flex justify-between text-xl">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold text-emerald-400">
                    {/* FIXED: Safe number conversion */}
                    ${parseFloat(bookingData.totalAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {bookingData.numAtvs} × ${parseFloat(bookingData.pricePerAtv || 0).toFixed(2)} per ATV
                  {bookingData.rideType === 'shared' && bookingData.sharedFee > 0 && (
                    <span> (includes ${parseFloat(bookingData.sharedFee || 0).toFixed(2)} shared fee per ATV)</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onBack}
              className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold text-lg transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Booking...' : 'Continue to Waivers'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;